import { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';

/**
 * AdminAiExtractionPage — `/admin/ai-extraction`
 *
 * Tunes the LLM that turns scraped content into structured events:
 * model name, temperature, few-shot example count, and the full system
 * prompt. Changes take effect within ~30s of saving.
 */

interface AiSettings {
  llmModelName?: string;
  llmTemperature?: number;
  llmFewShotExamplesCount?: number;
  extractionSystemPrompt?: string;
  extractionSystemPromptDefault?: string;
  preExtractGateEnabled?: boolean;
  preExtractGateMaxTextChars?: number;
  websiteInferredDateRejectEnabled?: boolean;
  similarityCacheEnabled?: boolean;
  similarityHammingMaxDistance?: number;
  similarityJaccardMinThreshold?: number;
  similarityLookbackDays?: number;
  docintelNeuralEnabled?: boolean;
  docintelNeuralAllowedVendors?: string;
  docintelNeuralConfidenceThreshold?: number;
  docintelNeuralModelId?: string;
}

export default function AdminAiExtractionPage() {
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    void api.get<AiSettings>('/api/mgmt/settings').then(setSettings).catch(() => null);
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.patch('/api/mgmt/settings', settings);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <h1>AI extraction</h1>

      {settings && (
        <section>
          <p className="text-muted">
            Controls the OpenAI / Azure OpenAI call that turns scraped content into structured event records.
            Changes take effect within ~30 seconds of saving.
          </p>
          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor="llm-model" className="form-label">Model name</label>
              <input
                id="llm-model"
                type="text"
                className="form-control"
                value={settings.llmModelName ?? 'gpt-4.1'}
                onChange={(e) => setSettings({ ...settings, llmModelName: e.target.value })}
                aria-describedby="llm-model-help"
              />
              <div id="llm-model-help" className="form-text">
                For Azure OpenAI, this is overridden by the deployment name configured in Key Vault.
              </div>
            </div>
            <div className="col-md-3">
              <label htmlFor="llm-temp" className="form-label">Temperature</label>
              <input
                id="llm-temp"
                type="number"
                step={0.1}
                min={0}
                max={2}
                className="form-control"
                value={settings.llmTemperature ?? 0}
                onChange={(e) => setSettings({ ...settings, llmTemperature: parseFloat(e.target.value) })}
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="llm-fewshot" className="form-label">Few-shot examples</label>
              <input
                id="llm-fewshot"
                type="number"
                min={0}
                max={30}
                className="form-control"
                value={settings.llmFewShotExamplesCount ?? 6}
                onChange={(e) => setSettings({ ...settings, llmFewShotExamplesCount: parseInt(e.target.value, 10) })}
                aria-describedby="llm-fewshot-help"
              />
              <div id="llm-fewshot-help" className="form-text">Past human decisions to attach. Default 6.</div>
            </div>
          </div>
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center">
              <label htmlFor="llm-prompt" className="form-label mb-0">Extraction system prompt</label>
              {settings.extractionSystemPromptDefault && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setSettings({ ...settings, extractionSystemPrompt: settings.extractionSystemPromptDefault })}
                >
                  Reset to default
                </button>
              )}
            </div>
            <textarea
              id="llm-prompt"
              className="form-control font-monospace"
              rows={20}
              value={settings.extractionSystemPrompt ?? ''}
              onChange={(e) => setSettings({ ...settings, extractionSystemPrompt: e.target.value })}
              aria-describedby="llm-prompt-help"
              spellCheck={false}
            />
            <div id="llm-prompt-help" className="form-text">
              Edit with care. The prompt expects the model to return JSON matching a strict schema (defined in code)
              and uses Jacksonville / Murray Hill specific neighborhood / venue context. Empty resets to the canonical default.
            </div>
          </div>
          <button className="btn btn-primary mt-3" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {savedAt && (
            <span className="ms-3 text-success" role="status">
              ✓ Saved {new Date(savedAt).toLocaleTimeString()}
            </span>
          )}

          <h2 className="h5 mt-5">Pre-extract gate</h2>
          <p className="text-muted small">
            Cheap deterministic check that runs <em>before</em> the LLM. Filters obvious non-events using regex/keyword rules so we don't burn tokens on noise.
          </p>
          <div className="form-check mb-2">
            <input
              id="pre-extract-gate-enabled"
              type="checkbox"
              className="form-check-input"
              checked={settings.preExtractGateEnabled !== false}
              onChange={(e) => setSettings({ ...settings, preExtractGateEnabled: e.target.checked })}
            />
            <label htmlFor="pre-extract-gate-enabled" className="form-check-label">Pre-extract gate enabled</label>
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="pre-extract-max-chars" className="form-label">Max input text chars</label>
              <input
                id="pre-extract-max-chars"
                type="number"
                min={1000}
                max={32000}
                className="form-control"
                value={settings.preExtractGateMaxTextChars ?? 8000}
                onChange={(e) => setSettings({ ...settings, preExtractGateMaxTextChars: parseInt(e.target.value, 10) })}
                aria-describedby="pre-extract-max-chars-help"
              />
              <div id="pre-extract-max-chars-help" className="form-text">Truncate post text to this many chars before gating. Default 8 000.</div>
            </div>
          </div>

          <h2 className="h5 mt-5">Date discipline</h2>
          <p className="text-muted small">
            Website pages have no publish date, so the relative-date drift guard can&rsquo;t check them. When enabled, any website event the extractor tags <code>dateConfidence=&quot;inferred&quot;</code> (a guessed date with no calendar anchor, usually a stale &ldquo;Trivia Friday&rdquo; listing) is auto-rejected instead of published.
          </p>
          <div className="form-check mb-2">
            <input
              id="website-inferred-date-reject"
              type="checkbox"
              className="form-check-input"
              checked={settings.websiteInferredDateRejectEnabled !== false}
              onChange={(e) => setSettings({ ...settings, websiteInferredDateRejectEnabled: e.target.checked })}
            />
            <label htmlFor="website-inferred-date-reject" className="form-check-label">Reject inferred-date website events</label>
          </div>

          <h2 className="h5 mt-5">Similarity cache</h2>
          <p className="text-muted small">
            Re-uses prior extraction results when a new raw post is near-identical to one we've already processed. Lower distance / higher Jaccard = stricter match.
          </p>
          <div className="form-check mb-2">
            <input
              id="sim-cache-enabled"
              type="checkbox"
              className="form-check-input"
              checked={settings.similarityCacheEnabled !== false}
              onChange={(e) => setSettings({ ...settings, similarityCacheEnabled: e.target.checked })}
            />
            <label htmlFor="sim-cache-enabled" className="form-check-label">Similarity cache enabled</label>
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="sim-hamming" className="form-label">Max Hamming distance</label>
              <input
                id="sim-hamming"
                type="number"
                min={0}
                max={16}
                className="form-control"
                value={settings.similarityHammingMaxDistance ?? 5}
                onChange={(e) => setSettings({ ...settings, similarityHammingMaxDistance: parseInt(e.target.value, 10) })}
                aria-describedby="sim-hamming-help"
              />
              <div id="sim-hamming-help" className="form-text">SimHash distance for image-bit fingerprints. Default 5.</div>
            </div>
            <div className="col-md-4">
              <label htmlFor="sim-jaccard" className="form-label">Min Jaccard threshold</label>
              <input
                id="sim-jaccard"
                type="number"
                step={0.01}
                min={0.5}
                max={1}
                className="form-control"
                value={settings.similarityJaccardMinThreshold ?? 0.95}
                onChange={(e) => setSettings({ ...settings, similarityJaccardMinThreshold: parseFloat(e.target.value) })}
                aria-describedby="sim-jaccard-help"
              />
              <div id="sim-jaccard-help" className="form-text">Token-set overlap threshold for text. Default 0.95.</div>
            </div>
            <div className="col-md-4">
              <label htmlFor="sim-lookback" className="form-label">Lookback (days)</label>
              <input
                id="sim-lookback"
                type="number"
                min={7}
                max={365}
                className="form-control"
                value={settings.similarityLookbackDays ?? 60}
                onChange={(e) => setSettings({ ...settings, similarityLookbackDays: parseInt(e.target.value, 10) })}
                aria-describedby="sim-lookback-help"
              />
              <div id="sim-lookback-help" className="form-text">How far back to search the cache. Default 60.</div>
            </div>
          </div>

          <h2 className="h5 mt-5">Document Intelligence — custom neural (Tier 0)</h2>
          <p className="text-muted small">
            Optional, default-off. When enabled, runs a per-vendor trained Doc Intelligence model <em>before</em> the deterministic grid parser for matching vendors. Requires the <code>S0</code> Doc Intel SKU.
          </p>
          <div className="form-check mb-2">
            <input
              id="docintel-neural-enabled"
              type="checkbox"
              className="form-check-input"
              checked={settings.docintelNeuralEnabled === true}
              onChange={(e) => setSettings({ ...settings, docintelNeuralEnabled: e.target.checked })}
            />
            <label htmlFor="docintel-neural-enabled" className="form-check-label">Custom-neural tier enabled</label>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor="docintel-neural-vendors" className="form-label">Allowed vendors (CSV)</label>
              <input
                id="docintel-neural-vendors"
                type="text"
                className="form-control"
                value={settings.docintelNeuralAllowedVendors ?? ''}
                onChange={(e) => setSettings({ ...settings, docintelNeuralAllowedVendors: e.target.value })}
                placeholder="e.g. murrayhillpreservation,brewstation"
                aria-describedby="docintel-neural-vendors-help"
              />
              <div id="docintel-neural-vendors-help" className="form-text">Only run the neural model for raws whose source handle/host matches one of these.</div>
            </div>
            <div className="col-md-3">
              <label htmlFor="docintel-neural-conf" className="form-label">Confidence threshold</label>
              <input
                id="docintel-neural-conf"
                type="number"
                step={0.05}
                min={0}
                max={1}
                className="form-control"
                value={settings.docintelNeuralConfidenceThreshold ?? 0.7}
                onChange={(e) => setSettings({ ...settings, docintelNeuralConfidenceThreshold: parseFloat(e.target.value) })}
                aria-describedby="docintel-neural-conf-help"
              />
              <div id="docintel-neural-conf-help" className="form-text">Below this, fall through to the next tier. Default 0.7.</div>
            </div>
            <div className="col-md-3">
              <label htmlFor="docintel-neural-model" className="form-label">Model ID</label>
              <input
                id="docintel-neural-model"
                type="text"
                className="form-control"
                value={settings.docintelNeuralModelId ?? ''}
                onChange={(e) => setSettings({ ...settings, docintelNeuralModelId: e.target.value })}
                placeholder="e.g. mhevents-grid-v3"
              />
            </div>
          </div>
        </section>
      )}
    </AdminLayout>
  );
}
